import { deleteObject, getDownloadURL, getMetadata, getStorage, listAll, ref, uploadString } from "firebase/storage";
//#region src/FirebaseStorageProvider.ts
/**
* Firebase implementation of IStorageProvider
*/
var FirebaseStorageProvider = class {
	constructor(app, config) {
		this.buckets = /* @__PURE__ */ new Map();
		this.defaultBucketName = "default";
		if (config?.defaultBucket) {
			const storage = getStorage(app);
			this.buckets.set(this.defaultBucketName, {
				storage,
				references: /* @__PURE__ */ new Map([["ROOT", ref(storage)]])
			});
		}
		config?.buckets?.forEach((bucketConfig) => {
			const storage = getStorage(app, bucketConfig.name);
			const rootRef = ref(storage);
			const references = /* @__PURE__ */ new Map([["ROOT", rootRef]]);
			bucketConfig.references.forEach((refPath) => {
				references.set(refPath, ref(rootRef, refPath));
			});
			this.buckets.set(bucketConfig.name, {
				storage,
				references
			});
		});
	}
	async uploadFile(request) {
		const bucket = this.getBucket(request.storageName);
		const baseRef = this.getReference(bucket, request.referenceKey);
		const filePath = request.subFolderName ? `${request.subFolderName}/${request.fileName}` : request.fileName;
		const fileRef = ref(baseRef, filePath);
		const metadata = { customMetadata: {
			comment: request.description,
			name: request.descriptionName,
			readonly: request.readonly ? "true" : "false"
		} };
		const result = await uploadString(fileRef, request.base64DataURL, "data_url", metadata);
		const downloadURL = await getDownloadURL(result.ref);
		return {
			fullPath: result.ref.fullPath,
			downloadURL
		};
	}
	async downloadFile(options) {
		const bucket = this.getBucket(options.storageName);
		const baseRef = this.getReference(bucket, options.referenceKey);
		const filePath = options.subFolderName ? `${options.subFolderName}/${options.fileName}` : options.fileName;
		const fileRef = ref(baseRef, filePath);
		return getDownloadURL(fileRef);
	}
	async deleteFile(options) {
		const bucket = this.getBucket(options.storageName);
		const baseRef = this.getReference(bucket, options.referenceKey);
		const filePath = options.subFolderName ? `${options.subFolderName}/${options.fileName}` : options.fileName;
		const fileRef = ref(baseRef, filePath);
		await deleteObject(fileRef);
	}
	async listFiles(options) {
		const bucket = this.getBucket(options.storageName);
		const baseRef = this.getReference(bucket, options.referenceKey);
		const targetRef = options.subFolderName ? ref(baseRef, options.subFolderName) : baseRef;
		const result = [];
		const listResult = await listAll(targetRef);
		for (const itemRef of listResult.items) {
			const doc = await this.itemRefToDocument(itemRef, options.storageName, options.referenceKey, options.subFolderName);
			result.push(doc);
		}
		for (const folderRef of listResult.prefixes) {
			const folderItems = await listAll(folderRef);
			for (const itemRef of folderItems.items) {
				const doc = await this.itemRefToDocument(itemRef, options.storageName, options.referenceKey, folderRef.name);
				result.push(doc);
			}
		}
		return result;
	}
	async getFile(options) {
		try {
			const bucket = this.getBucket(options.storageName);
			const baseRef = this.getReference(bucket, options.referenceKey);
			const filePath = options.subFolderName ? `${options.subFolderName}/${options.fileName}` : options.fileName;
			const fileRef = ref(baseRef, filePath);
			const metadata = await getMetadata(fileRef);
			return this.metadataToDocument(metadata, fileRef, options.storageName, options.referenceKey, options.subFolderName);
		} catch {
			return null;
		}
	}
	getBucket(storageName) {
		const bucket = this.buckets.get(storageName) || this.buckets.get(this.defaultBucketName);
		if (!bucket) throw new Error(`Storage bucket not found: ${storageName}`);
		return bucket;
	}
	getReference(bucket, referenceKey) {
		const reference = bucket.references.get(referenceKey) || bucket.references.get("ROOT");
		if (!reference) throw new Error(`Storage reference not found: ${referenceKey}`);
		return reference;
	}
	async itemRefToDocument(itemRef, storageName, refKey, subFolderName) {
		const metadata = await getMetadata(itemRef);
		return this.metadataToDocument(metadata, itemRef, storageName, refKey, subFolderName);
	}
	metadataToDocument(metadata, itemRef, storageName, refKey, subFolderName) {
		return {
			storageName,
			refKey,
			fileName: itemRef.name,
			filePath: itemRef.fullPath,
			subFolderName,
			documentType: metadata.contentType ?? void 0,
			description: metadata.customMetadata?.comment,
			descriptionName: metadata.customMetadata?.name,
			creation: new Date(metadata.timeCreated),
			creator: metadata.customMetadata?.creator,
			size: metadata.size,
			readonly: metadata.customMetadata?.readonly === "true",
			metaData: metadata
		};
	}
	/**
	* Get the underlying Firebase Storage instance for a bucket
	*/
	getFirebaseStorage(storageName) {
		return (storageName ? this.buckets.get(storageName) : this.buckets.get(this.defaultBucketName))?.storage;
	}
};
//#endregion
export { FirebaseStorageProvider };
